import { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Button, Group, Paper, Skeleton, Stack, Switch, Text, Textarea, Title } from '@mantine/core';
import type { MyParishView } from '@/types/myParish';
import styles from './MyParish.module.sass';
import { ChurchContext } from '@/shared';
import { useTabStatusContext } from '@/hooks';
import { BULLETIN_TYPE, CHURCH_TYPE } from '@/types';
import { EditMyParishModal, Icon } from '@/components';
import { useMyParish, useUpdateMyParishMutation } from '@/hooks/organization-programs';

export const MyParish = () => {
  const { id: churchId } = useParams<{ id: string }>();
  const { church } = useContext(ChurchContext);
  const { refreshTabStatus } = useTabStatusContext();
  const { data: myParishData, isLoading, isFetching, isError } = useMyParish(churchId);
  const updateMyParishMutation = useUpdateMyParishMutation(churchId ?? '');
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  const LAYOUT = {
    ARROW_LEFT_ICON_SIZE: 16,
    LOADING_PAPER_MIN_HEIGHT: 200,
    MIN_ROWS: 3,
    MAX_ROWS: 6,
  };

  if (!churchId) {
    return (
      <Paper p="xl" radius="md" w="100%">
        <Text c="red">Church ID not found</Text>
      </Paper>
    );
  }

  if (isLoading || isFetching) {
    return (
      <Paper p="xl" radius="md" w="100%">
        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <Skeleton height={28} width={250} radius="sm" />
            <Skeleton height={30} width={60} radius="sm" />
          </Group>
          <Group gap="md" align="center">
            <Skeleton height={20} width={60} radius="sm" />
            <Skeleton height={22} width={80} radius="xl" />
          </Group>
          <Group gap="md" align="center">
            <Skeleton height={20} width={100} radius="sm" />
            <Skeleton height={22} width={80} radius="xl" />
          </Group>
          <Stack gap="xs">
            <Skeleton height={20} width={200} radius="sm" />
            <Skeleton height={80} width="100%" radius="sm" />
          </Stack>
        </Stack>
      </Paper>
    );
  }

  if (isError || !myParishData) {
    return (
      <Paper p="xl" radius="md" w="100%">
        <Text c="red">Failed to load myParish data</Text>
      </Paper>
    );
  }

  const handleEditMyParish = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleSaveMyParish = async (updatedData: MyParishView) => {
    await updateMyParishMutation.mutateAsync({
      isActive: updatedData.isActive,
      isHiddenFromApp: updatedData.isHiddenFromApp,
      isDioceseControl: updatedData.isDioceseControl,
      isEnhanced: updatedData.isEnhanced,
      bulletin: updatedData.bulletin,
      comment: updatedData.comment,
    });
    setIsEditModalOpen(false);
    refreshTabStatus();
  };

  return (
    <>
      <Paper p="xl" radius="md" w="100%">
        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <Title order={3}>myParish Information</Title>
            <Button
              onClick={handleEditMyParish}
              variant="default"
              size="xs"
              leftSection={<Icon name="IconEdit" size={LAYOUT.ARROW_LEFT_ICON_SIZE} />}
            >
              Edit
            </Button>
          </Group>
          <Group gap="md" align="center">
            <Group gap="md" align="center" flex="1">
              <Text className={styles.label} size="sm">
                Status:
              </Text>
              <Badge color={myParishData?.isActive ? 'green' : 'red'} variant="light">
                {myParishData?.isActive ? 'Active' : 'Inactive'}
              </Badge>
              {myParishData?.isHiddenFromApp && (
                <Text size="sm" c="red">
                  Do not display in App
                </Text>
              )}
            </Group>
            {myParishData?.isDioceseControl && church?.type === CHURCH_TYPE.DIOCESE && (
              <Group gap="md" align="center" flex="1">
                <Text size="sm">Diocese Control</Text>
                <Switch checked={myParishData?.isDioceseControl} readOnly />
              </Group>
            )}
          </Group>

          <Group gap="md" align="center">
            <Group gap="md" align="center" flex="1">
              <Text className={styles.label} size="sm">
                Bulletin Type:
              </Text>
              <Badge variant="default">
                {myParishData.bulletin.type === BULLETIN_TYPE.DIOCESAN && 'Diocesan'}
                {myParishData.bulletin.type === BULLETIN_TYPE.LPI && 'LPI'}
                {myParishData.bulletin.type === BULLETIN_TYPE.OTHER && 'Other'}
                {myParishData.bulletin.type === BULLETIN_TYPE.NONE && 'None'}
              </Badge>
            </Group>
            {myParishData.bulletin.type !== BULLETIN_TYPE.NONE && myParishData.bulletin.number && (
              <Group gap="md" align="center" flex="1">
                <Text className={styles.label} size="sm">
                  Bulletin Number:
                </Text>
                <Text>{myParishData.bulletin.number}</Text>
              </Group>
            )}
          </Group>

          <Group gap="md" align="center">
            <Group gap="md" align="center" flex="1">
              <Text className={styles.label} size="sm">
                Enhanced:
              </Text>
              <Switch checked={myParishData.isEnhanced} readOnly />
            </Group>
          </Group>

          <Stack gap="xs" className={styles.commentsSection}>
            <Text className={styles.label} size="sm">
              Comments (Internal Use Only):
            </Text>
            <Textarea
              value={myParishData?.comment || ''}
              placeholder="Enter internal comments..."
              minRows={LAYOUT.MIN_ROWS}
              maxRows={LAYOUT.MAX_ROWS}
              readOnly
            />
          </Stack>
        </Stack>
      </Paper>

      <EditMyParishModal
        opened={isEditModalOpen}
        myParishData={myParishData}
        onClose={handleCloseEditModal}
        onSave={handleSaveMyParish}
      />
    </>
  );
};
